/** Small, drifting boats. Models share GPU resources; each hull has real contact physics. */
import { BoxGeometry, Group, Mesh, MeshStandardMaterial, Vector3 } from 'three/webgpu'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { Vessel } from '../physics/Vessel.ts'
import { closestPointOnObstacle, type Obstacle } from './Obstacle.ts'
import type { HullWaterProfile, HullWaterSource } from './HullWaterMasks.ts'

const _propulsion = new Vector3()
const _propulsionPoint = new Vector3()

export const TRAFFIC_TYPES = [
  { name: 'fishing-boat', length: 12, beam: 3.8, mass: 18000, colour: '#22638a' },
  { name: 'sailing-yacht', length: 11, beam: 3.4, mass: 7000, colour: '#ede9d8' },
  { name: 'motor-launch', length: 8, beam: 2.8, mass: 4500, colour: '#233d53' },
] as const

export class TrafficBoat extends Vessel {
  readonly group = new Group()
  readonly waterExclusion: HullWaterSource
  private course = 0
  private coursePhase = 0
  private navigationTime = 0
  private readonly cruiseSpeed: number
  constructor(readonly style: number) {
    const t = TRAFFIC_TYPES[style]
    super({ name: t.name, mass: t.mass, length: t.length, beam: t.beam, draft: .9,
      dragForward: 500, dragLateral: 5000, dragYaw: 15000, lateralResistanceOffset: .3,
      buoyancySamples: [3, 2], pitchDamping: 1.1, rollDamping: 1.1 })
    this.cruiseSpeed = style === 1 ? 2.6 : style === 2 ? 3.2 : 3.7
    this.group.name = t.name
    const profile: HullWaterProfile = [[-.5,.72],[-.35,.96],[0,1],[.3,.75],[.44,.36],[.5,.035]]
      .map(([z,w]) => [z*t.length,w*t.beam/2,.8+Math.max(0,z)*.65])
    this.waterExclusion = { object: this.group, profile }
  }

  /** Give this boat a deterministic, gently wandering route. */
  setCourse(heading: number, phase: number): void {
    this.course = heading
    this.coursePhase = phase
  }

  setNavigationTime(time: number): void {
    this.navigationTime = time
  }

  protected override applyControlForces(_dt: number): void {
    // Traffic is independently powered. A proportional throttle settles at a
    // modest cruising speed instead of leaving the boats as static scenery.
    const speedError = this.cruiseSpeed - this.forwardSpeed
    const thrust = Math.max(-this.config.mass * 0.45, Math.min(this.config.mass * 0.9, speedError * this.config.mass * 0.9))
    _propulsion.set(0, 0, thrust)
    _propulsionPoint.set(0, 0, -this.config.length * 0.42)
    this.applyBodyForce(_propulsion, _propulsionPoint)

    // Small course changes make the sea feel inhabited without sending boats
    // into tight turns. The autopilot also gently recovers from collisions.
    const targetHeading = this.course + Math.sin(this.navigationTime * 0.11 + this.coursePhase) * 0.18
    let error = targetHeading - this.yaw
    error = Math.atan2(Math.sin(error), Math.cos(error))
    const yawInertia = (this.config.mass * this.config.length * this.config.length / 12) * 1.6
    this.applyYawTorque((error * 0.32 - this.yawRate) * yawInertia * 1.5)
  }
  syncVisual(): void { this.group.position.copy(this.position); this.group.quaternion.copy(this.quaternion) }
}

function disposeTree(root: Group): void {
  root.traverse(o => { if (o instanceof Mesh) { o.geometry.dispose(); for (const m of Array.isArray(o.material) ? o.material : [o.material]) m.dispose() } })
}

export class SeaTraffic {
  readonly group = new Group()
  readonly boats: TrafficBoat[] = []
  readonly ready: Promise<void>
  private disposed = false
  private templates: Group[] = []
  private readonly fallbackGeometry = new BoxGeometry(1, 1, 1)
  private readonly fallbackMaterials = TRAFFIC_TYPES.map(t => new MeshStandardMaterial({ color: t.colour, roughness: .6 }))
  private seed: number
  private readonly nearest = new Vector3()

  constructor(private readonly player: Vessel, private readonly obstacles: readonly Obstacle[] = [], seed = Math.floor(Math.random()*0xffffffff), protectedBodies: readonly Vessel[] = []) {
    this.seed = seed >>> 0
    this.group.name = 'small-boat-traffic'
    for (let i=0;i<12;i++) {
      const boat = new TrafficBoat(i%3)
      const fallback = new Mesh(this.fallbackGeometry, this.fallbackMaterials[i%3])
      fallback.scale.set(boat.config.beam,.9,boat.config.length); fallback.position.y=.35
      boat.group.add(fallback)
      if (!this.place(boat, false, protectedBodies)) continue
      this.boats.push(boat); this.group.add(boat.group)
    }
    this.ready = this.load()
  }

  private random(): number { this.seed = (Math.imul(this.seed,1664525)+1013904223)>>>0; return this.seed/4294967296 }

  private place(boat: TrafficBoat, recycled: boolean, protectedBodies: readonly Vessel[]): boolean {
    for (let attempt=0;attempt<160;attempt++) {
      // Near boats are visible on departure, while recycled boats appear beyond the near view.
      const nearDeparture = !recycled && this.boats.length < 3 && attempt < 50
      const distance = recycled ? 950+this.random()*300 : nearDeparture
        ? this.player.config.length*.6+95+this.boats.length*90+this.random()*55
        : this.player.config.length*.6+65+this.random()*550
      const angle = nearDeparture ? this.player.yaw+(this.random()-.5)*.85 : this.random()*Math.PI*2
      const x = this.player.position.x+Math.sin(angle)*distance, z = this.player.position.z+Math.cos(angle)*distance
      const safe = [this.player,...protectedBodies,...this.boats].every(body => body===boat || Math.hypot(body.position.x-x,body.position.z-z) > body.config.length*.55+boat.config.length*.6+25)
      if (!safe || this.obstacles.some(o => closestPointOnObstacle(o,x,z,this.nearest).distanceTo(new Vector3(x,0,z)) < boat.config.length+15)) continue
      boat.position.set(x,0,z);boat.velocity.set(0,0,0);boat.yaw=this.random()*Math.PI*2;boat.yawRate=0
      boat.setCourse(boat.yaw, this.random()*Math.PI*2)
      boat.step(0,0);boat.syncVisual();return true
    }
    return false
  }

  private async load(): Promise<void> {
    // Settle all requests so a failed download cannot orphan the successful siblings.
    const loaded = await Promise.allSettled(TRAFFIC_TYPES.map(t => new GLTFLoader().loadAsync(`/models/traffic/${t.name}.glb`)))
    loaded.forEach((result,style) => {
      if (result.status === 'rejected') { if (!this.disposed) console.warn(`Traffic model unavailable: ${TRAFFIC_TYPES[style].name}`,result.reason); return }
      const template = result.value.scene
      if (this.disposed) { disposeTree(template);return }
      template.rotation.y=-Math.PI/2;this.templates.push(template)
      for (const boat of this.boats.filter(b=>b.style===style)) { boat.group.clear();boat.group.add(template.clone(true)) }
    })
  }

  step(dt: number, elapsed: number, otherBodies: readonly Vessel[] = []): boolean {
    const collisionCount = this.player.collisions.length
    for (const boat of this.boats) {
      if (boat.position.distanceTo(this.player.position)>1600) this.place(boat,true,otherBodies)
      boat.setNavigationTime(elapsed)
      boat.step(dt,elapsed)
      this.player.collideWithVessel(boat)
      for (const body of otherBodies) body.collideWithVessel(boat)
      boat.resolveCollisions(this.obstacles)
    }
    for (let i=0;i<this.boats.length;i++) for(let j=0;j<i;j++) this.boats[i].collideWithVessel(this.boats[j])
    return this.player.collisions.length > collisionCount
  }

  frame(): void { for(const boat of this.boats) boat.syncVisual() }
  waterExclusions(): HullWaterSource[] {
    return [...this.boats].sort((a,b)=>a.position.distanceToSquared(this.player.position)-b.position.distanceToSquared(this.player.position))
      .slice(0,2).map(b=>b.waterExclusion)
  }
  dispose(): void {
    this.disposed=true;this.group.removeFromParent();this.group.clear();this.boats.length=0
    this.templates.forEach(disposeTree);this.templates=[];this.fallbackGeometry.dispose();this.fallbackMaterials.forEach(m=>m.dispose())
  }
}
